import { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, FileText, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GHGInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/ghg-inventory`);
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching GHG inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScopeColor = (scope) => {
    switch (scope) {
      case 'Scope 1': return 'bg-[#C85A32] text-white';
      case 'Scope 2': return 'bg-[#7898A5] text-white';
      case 'Scope 3': return 'bg-[#8B9D8F] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const groupedInventory = inventory.reduce((acc, item) => {
    if (!acc[item.scope]) acc[item.scope] = [];
    acc[item.scope].push(item);
    return acc;
  }, {});

  const scopeTotals = Object.entries(groupedInventory).map(([scope, items]) => ({
    scope,
    total: items.reduce((sum, item) => sum + item.emissions, 0)
  }));

  const totalEmissions = scopeTotals.reduce((sum, s) => sum + s.total, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="ghg-inventory-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          GHG Inventory Table
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Structured greenhouse gas inventory based on GHG Protocol Corporate Standard
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {scopeTotals.map((s) => (
          <Card key={s.scope} className="border-[#E5E1D8]" data-testid={`scope-total-${s.scope.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="pt-4">
              <Badge className={`${getScopeColor(s.scope)} mb-2`}>{s.scope}</Badge>
              <p className="text-2xl font-bold text-[#111814]">{s.total.toLocaleString()}</p>
              <p className="text-sm text-[#6B7280]">kg CO₂e</p>
            </CardContent>
          </Card>
        ))}
        <Card className="border-[#1A362D] border-2" data-testid="total-emissions-summary">
          <CardContent className="pt-4">
            <Badge className="bg-[#1A362D] text-white mb-2">Total</Badge>
            <p className="text-2xl font-bold text-[#1A362D]">{totalEmissions.toLocaleString()}</p>
            <p className="text-sm text-[#6B7280]">kg CO₂e</p>
          </CardContent>
        </Card>
      </div>

      {/* Protocol Badge */}
      <Card className="border-[#E5E1D8] mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1A362D]" />
            <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Based on GHG Protocol
            </CardTitle>
          </div>
          <CardDescription>
            This inventory follows the GHG Protocol Corporate Accounting and Reporting Standard, the most widely used international accounting tool for government and business leaders.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Inventory Table */}
      <Card className="border-[#E5E1D8]" data-testid="ghg-inventory-table">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Table className="w-5 h-5 text-[#1A362D]" />
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Detailed Inventory</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E1D8]">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Scope</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Category</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Source</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Activity Data</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Unit</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">EF</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Emissions (kg CO₂e)</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item, index) => (
                  <tr key={index} className="border-b border-[#F2F0EB] hover:bg-[#F9F8F6] transition-colors">
                    <td className="py-3 px-4">
                      <Badge className={getScopeColor(item.scope)}>{item.scope}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#4B5563]">{item.category}</td>
                    <td className="py-3 px-4 text-sm font-medium text-[#111814]">{item.source}</td>
                    <td className="py-3 px-4 text-sm text-right text-[#111814]">{item.activity_data.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-right text-[#6B7280]">{item.unit}</td>
                    <td className="py-3 px-4 text-sm text-right text-[#6B7280]">{item.emission_factor}</td>
                    <td className="py-3 px-4 text-sm text-right font-semibold text-[#111814]">{item.emissions.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#F9F8F6]">
                  <td colSpan={6} className="py-3 px-4 text-sm font-semibold text-[#111814]">Total Emissions</td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-[#1A362D]">{totalEmissions.toLocaleString()} kg CO₂e</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Methodology Note */}
      <div className="mt-6 p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-2">Methodology</p>
        <p className="text-sm text-[#4B5563] leading-relaxed">
          Emissions calculated using: <span className="font-semibold">Activity Data × Emission Factor = GHG Emissions</span>. 
          Emission factors sourced from IPCC, DEFRA, and national grid factors. Scope definitions follow GHG Protocol Corporate Standard.
        </p>
      </div>
    </div>
  );
};

export default GHGInventory;
